import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const PWAConfigSection = () => {
  const [pwaConfig, setPwaConfig] = useState({
    installPrompt: true,
    offlineMode: true,
    backgroundSync: true,
    pushNotifications: true
  });

  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    // Check if PWA is supported
    setIsSupported('serviceWorker' in navigator && 'PushManager' in window);
    
    // Check if already installed
    setIsInstalled(window.matchMedia('(display-mode: standalone)')?.matches);

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e) => {
      e?.preventDefault();
      setInstallPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallPWA = async () => {
    if (!installPrompt) return;

    const result = await installPrompt?.prompt();
    console.log('Install prompt result:', result);
    
    setInstallPrompt(null);
    
    if (result?.outcome === 'accepted') {
      setIsInstalled(true);
    }
  };

  const handleConfigChange = (field, value) => {
    setPwaConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      alert('This browser does not support notifications');
      return;
    }

    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      new Notification('SecureLock Admin', {
        body: 'Push notifications are now enabled!',
        icon: '/favicon.ico'
      });
    }
  };

  const getInstallationStatus = () => {
    if (isInstalled) {
      return {
        status: 'installed',
        text: 'App is installed',
        color: 'text-success',
        icon: 'CheckCircle'
      };
    } else if (installPrompt) {
      return {
        status: 'available',
        text: 'Installation available',
        color: 'text-accent',
        icon: 'Download'
      };
    } else {
      return {
        status: 'unavailable',
        text: 'Installation not available',
        color: 'text-muted-foreground',
        icon: 'AlertCircle'
      };
    }
  };

  const installStatus = getInstallationStatus();

  return (
    <div className="bg-card rounded-lg border border-border p-6 security-shadow-card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground flex items-center space-x-2">
            <Icon name="Smartphone" size={20} className="text-primary" />
            <span>PWA Configuration</span>
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Progressive Web App settings for mobile installation and offline functionality
          </p>
        </div>
        {installPrompt && !isInstalled && (
          <Button
            variant="default"
            iconName="Download"
            iconPosition="left"
            onClick={handleInstallPWA}
            className="security-transition"
          >
            Install App
          </Button>
        )}
      </div>
      {/* Installation Status */}
      <div className="mb-6 p-4 bg-muted/30 rounded-lg border border-border/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Icon 
              name={installStatus?.icon} 
              size={20} 
              className={installStatus?.color}
            />
            <div>
              <h4 className="text-sm font-medium text-foreground">Installation Status</h4>
              <p className={`text-sm ${installStatus?.color}`}>{installStatus?.text}</p>
            </div>
          </div>
          
          {isSupported ? (
            <div className="flex items-center space-x-2 text-success">
              <Icon name="CheckCircle" size={16} />
              <span className="text-sm">PWA Supported</span>
            </div>
          ) : (
            <div className="flex items-center space-x-2 text-warning">
              <Icon name="AlertTriangle" size={16} />
              <span className="text-sm">Limited Support</span>
            </div>
          )}
        </div>
      </div>
      {/* PWA Features */}
      <div className="space-y-6">
        <div>
          <h4 className="text-md font-medium text-foreground mb-4">PWA Features</h4>
          <div className="space-y-4">
            <Checkbox
              label="Show install prompt"
              description="Display installation banner for supported browsers"
              checked={pwaConfig?.installPrompt}
              onChange={(e) => handleConfigChange('installPrompt', e?.target?.checked)}
            />
            
            <Checkbox
              label="Offline mode"
              description="Enable basic functionality when internet is unavailable"
              checked={pwaConfig?.offlineMode}
              onChange={(e) => handleConfigChange('offlineMode', e?.target?.checked)}
            />
            
            <Checkbox
              label="Background sync"
              description="Sync data when connection is restored"
              checked={pwaConfig?.backgroundSync}
              onChange={(e) => handleConfigChange('backgroundSync', e?.target?.checked)}
            />
            
            <div className="flex items-center justify-between">
              <Checkbox
                label="Push notifications"
                description="Receive security alerts even when app is closed"
                checked={pwaConfig?.pushNotifications}
                onChange={(e) => handleConfigChange('pushNotifications', e?.target?.checked)}
              />
              {pwaConfig?.pushNotifications && (
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Bell"
                  onClick={requestNotificationPermission}
                  className="ml-4"
                >
                  Test Notification
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Optimization */}
        <div>
          <h4 className="text-md font-medium text-foreground mb-4 flex items-center space-x-2">
            <Icon name="Monitor" size={16} className="text-accent" />
            <span>Mobile Optimization</span>
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Smartphone" size={16} className="text-success" />
                <span className="text-sm font-medium text-foreground">Mobile Features</span>
              </div>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Bottom tab navigation</li>
                <li>• Touch-friendly controls</li>
                <li>• Responsive design</li>
                <li>• Gesture support</li>
              </ul>
            </div>
            
            <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Zap" size={16} className="text-warning" />
                <span className="text-sm font-medium text-foreground">Performance</span>
              </div>
              <ul className="text-xs text-muted-foreground space-y-1">
                <li>• Lazy loading</li>
                <li>• Image optimization</li>
                <li>• Caching strategy</li>
                <li>• Minimal bundle size</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Installation Instructions */}
        <div className="p-4 bg-accent/10 rounded-lg border border-accent/20">
          <h5 className="text-sm font-medium text-accent mb-3 flex items-center space-x-2">
            <Icon name="Info" size={16} />
            <span>Installation Instructions</span>
          </h5>
          <div className="space-y-3 text-xs text-muted-foreground">
            <div>
              <p className="font-medium text-foreground mb-1">Chrome/Edge (Desktop):</p>
              <p>Click the install icon in the address bar or use the "Install App" button above</p>
            </div>
            <div>
              <p className="font-medium text-foreground mb-1">Safari (iOS):</p>
              <p>Tap the share button and select "Add to Home Screen"</p>
            </div>
            <div>
              <p className="font-medium text-foreground mb-1">Chrome (Android):</p>
              <p>Tap the menu button and select "Add to Home Screen" or "Install App"</p>
            </div>
          </div>
        </div>

        {/* Current Configuration */}
        <div className="p-4 bg-success/10 rounded-lg border border-success/20">
          <h5 className="text-sm font-medium text-success mb-2 flex items-center space-x-2">
            <Icon name="Settings" size={16} />
            <span>Current PWA Configuration</span>
          </h5>
          <div className="text-xs text-muted-foreground space-y-1">
            <p>• Install prompt: {pwaConfig?.installPrompt ? 'Enabled' : 'Disabled'}</p>
            <p>• Offline mode: {pwaConfig?.offlineMode ? 'Enabled' : 'Disabled'}</p>
            <p>• Background sync: {pwaConfig?.backgroundSync ? 'Enabled' : 'Disabled'}</p>
            <p>• Push notifications: {pwaConfig?.pushNotifications ? 'Enabled' : 'Disabled'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAConfigSection;